import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { usePatientRole } from '@/hooks/usePatientRole';
import { AppLayout } from '@/components/layout/AppLayout';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Pill,
  AlertTriangle,
  AlertCircle,
  Package,
  Trash2,
  Droplets,
  Utensils,
  Stethoscope,
  Wrench,
  CheckCircle2,
  Save,
  Edit2
} from 'lucide-react';
import type { InventoryItem, ItemType, StockStatus } from '@/types/inventory';

const itemTypeIconMap: Record<ItemType, React.ReactNode> = {
  medication: <Pill className="w-8 h-8" />,
  hygiene: <Droplets className="w-8 h-8" />,
  nutrition: <Utensils className="w-8 h-8" />,
  medical_supply: <Stethoscope className="w-8 h-8" />,
  equipment: <Wrench className="w-8 h-8" />,
  other: <Package className="w-8 h-8" />
};

export default function ItemDetail() {
  const { t } = useTranslation();
  const { patientId, itemId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canEdit, canEditInventory, isCaregiver } = usePatientRole(patientId);

  const [item, setItem] = useState<InventoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [editName, setEditName] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const canManageInventory = canEdit || canEditInventory;

  const statusConfig: Record<StockStatus, { label: string; icon: React.ReactNode; colors: string }> = {
    ok: {
      label: t('inventory.status.ok'),
      icon: <CheckCircle2 className="w-4 h-4" />,
      colors: 'bg-success/10 text-success border-success/50'
    },
    low_stock: {
      label: t('inventory.status.low_stock'),
      icon: <AlertTriangle className="w-4 h-4" />,
      colors: 'bg-warning/10 text-warning border-warning/50'
    },
    zero_stock: {
      label: t('inventory.status.zero_stock'),
      icon: <AlertCircle className="w-4 h-4" />,
      colors: 'bg-destructive/10 text-destructive border-destructive/50'
    }
  };

  useEffect(() => {
    if (itemId) {
      fetchData();
    }
  }, [itemId]);

  const fetchData = async () => {
    try {
      const { data: itemData, error: itemError } = await supabase
        .from('inventory')
        .select('*')
        .eq('id', itemId)
        .single();

      if (itemError) throw itemError;
      setItem(itemData as InventoryItem);
      setEditName(itemData.item_name);
      setEditUnit(itemData.unit);
      setEditNotes(itemData.notes || '');
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate(`/app/inventory/${patientId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus: StockStatus) => {
    if (!item || !user) return;

    setUpdatingStatus(true);

    try {
      const { error } = await supabase
        .from('inventory')
        .update({ status: newStatus })
        .eq('id', item.id);

      if (error) throw error;

      setItem(prev => prev ? { ...prev, status: newStatus } : null);

      if (newStatus === 'low_stock' || newStatus === 'zero_stock') {
        toast.success(t('inventory.notifications.alertSent'));
      } else {
        toast.success(t('inventory.notifications.statusUpdated'));
      }

      captureEvent('inventory_item_status_updated', {
        patient_id: patientId,
        item_id: item.id,
        item_name: item.item_name,
        new_status: newStatus
      });

    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('common.error'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSave = async () => {
    if (!item || !editName.trim()) {
      toast.error(t('inventory.notifications.nameRequired'));
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('inventory')
        .update({
          item_name: editName.trim(),
          unit: editUnit.trim(),
          notes: editNotes.trim() || null
        })
        .eq('id', item.id);

      if (error) throw error;

      setItem(prev => prev ? {
        ...prev,
        item_name: editName.trim(),
        unit: editUnit.trim(),
        notes: editNotes.trim() || null
      } : null);

      setIsEditing(false);
      toast.success(t('inventory.notifications.itemUpdated'));

      captureEvent('inventory_item_updated', {
        patient_id: patientId,
        item_id: item.id,
        item_name: editName.trim()
      });

    } catch (error) {
      console.error('Error saving:', error);
      toast.error(t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;

    try {
      const { error } = await supabase
        .from('inventory')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      toast.success(t('inventory.notifications.itemRemoved'));

      captureEvent('inventory_item_deleted', {
        patient_id: patientId,
        item_id: item.id,
        item_name: item.item_name
      });

      navigate(`/app/inventory/${patientId}`);

    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(t('common.error'));
    }
  };

  if (isLoading || !item) {
    return (
      <AppLayout title={t('common.details')} showBack>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      </AppLayout>
    );
  }

  const config = statusConfig[item.status];
  const itemTypeLabel = t(`inventory.itemType.${item.item_type}`);

  return (
    <AppLayout
      title={item.item_name}
      showBack
      onBack={() => navigate(`/app/inventory/${patientId}`)}
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Item Card */}
        <Card className={item.status !== 'ok' ? `border ${item.status === 'zero_stock' ? 'border-destructive/50' : 'border-warning/50'}` : ''}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full ${config.colors}`}>
                {itemTypeIconMap[item.item_type]}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[12px]">
                      {itemTypeLabel}
                    </Badge>
                    <Badge variant="outline" className={`${config.colors} text-[12px]`}>
                      {config.icon}
                      <span className="ml-1">{config.label}</span>
                    </Badge>
                  </div>
                  {canManageInventory && !isEditing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-3 mt-3">
                    <div>
                      <Label htmlFor="name">{t('common.name')}</Label>
                      <Input
                        id="name"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit">{t('common.unit')}</Label>
                      <Input
                        id="unit"
                        value={editUnit}
                        onChange={e => setEditUnit(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">{t('common.notes')}</Label>
                      <Textarea
                        id="notes"
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                        className="flex-1"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex-1"
                      >
                        <Save className="w-4 h-4 mr-1" />
                        {isSaving ? t('common.saving') : t('common.save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-bold mt-2">{item.item_name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {item.unit}
                    </p>

                    {item.notes && (
                      <p className="text-sm text-muted-foreground mt-3 p-3 bg-muted rounded-lg">
                        {item.notes}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Actions */}
        {(isCaregiver || canManageInventory) && !isEditing && (
          <Card>
            <CardContent className="pt-4">
              <Label className="text-sm font-medium">{t('inventory.actions.changeStatus')}</Label>
              <div className="grid grid-cols-1 gap-2 mt-3">
                {item.status !== 'ok' && (
                  <Button
                    variant="outline"
                    className="w-full border-success/50 text-success hover:bg-success/10"
                    onClick={() => updateStatus('ok')}
                    disabled={updatingStatus}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {t('inventory.actions.informReceipt')}
                  </Button>
                )}
                {item.status === 'ok' && (
                  <>
                    <Button
                      variant="outline"
                      className="w-full border-warning/50 text-warning hover:bg-warning/10"
                      onClick={() => updateStatus('low_stock')}
                      disabled={updatingStatus}
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      {t('inventory.actions.informLowStock')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
                      onClick={() => updateStatus('zero_stock')}
                      disabled={updatingStatus}
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {t('inventory.actions.informZeroStock')}
                    </Button>
                  </>
                )}
                {item.status === 'low_stock' && (
                  <Button
                    variant="outline"
                    className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
                    onClick={() => updateStatus('zero_stock')}
                    disabled={updatingStatus}
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {t('inventory.actions.informZeroStock')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Button */}
        {canManageInventory && !isEditing && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('inventory.actions.removeFromInventory')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('inventory.notifications.confirmDeleteTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('inventory.notifications.confirmDeleteDescription', { name: item.item_name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('common.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </AppLayout>
  );
}